import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Card } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { RootState } from '../../store';
import { AuditLogExplorerService } from '../../services/auditLogExplorer.service';
import { AuditLogExplorerTypes } from '../../types/auditLogExplorer.types';
import { AuditFilterBar } from './audit-filter-bar';
import { AuditResultsTable } from './audit-results-table';
import { AuditDetailDrawer } from './audit-detail-drawer';
import {
    AppliedPill, EMPTY_FILTERS, FilterState, buildAppliedPills, buildSearchRequest, clearFilterKey,
} from './helpers';
import { actionLabel } from '../../builders/audit-log-explorer-builders';
import { ALE } from './tokens';

const InfoBanner: React.FC = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', bgcolor: ALE.infoBg, color: ALE.infoT, borderRadius: '8px', p: '10px 14px', fontSize: '12.5px', fontWeight: 500, mt: '18px' }}>
        <InfoOutlinedIcon sx={{ fontSize: 17 }} />
        <span>
            Not a 100% mirror — high-volume endpoints (heartbeats, location updates) are sampled 5–10% and some records are delayed.
            Identify customers by subscription/user ID, never name or phone.
        </span>
    </Box>
);

const downloadCsv = (items: AuditLogExplorerTypes.ListRow[]) => {
    const cols: (keyof AuditLogExplorerTypes.ListRow)[] = [
        'timestamp', 'method', 'statusCode', 'endpoint', 'path', 'apiSource',
        'subscriptionId', 'userId', 'userType', 'responseTimeMs', 'errorMessage',
    ];
    const escape = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const header = cols.join(',');
    const rows = items.map((r) => cols.map((c) => escape(r[c])).join(','));
    const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-logs-${new Date().toISOString().slice(0, 19)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
};

export const AuditLogExplorer: React.FC<{ active?: boolean }> = ({ active = true }) => {
    const { result, detail, loading } = useSelector((s: RootState) => s.auditLogExplorer);

    const [draft, setDraft] = useState<FilterState>(EMPTY_FILTERS);
    // `submitted` is the snapshot the current query was built from; editing the
    // bar does not re-query until Search is pressed (or a pill is removed).
    const [submitted, setSubmitted] = useState<FilterState>(EMPTY_FILTERS);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(50);
    const [sortBy, setSortBy] = useState<AuditLogExplorerTypes.SortBy>('timestamp');
    const [sortOrder, setSortOrder] = useState<AuditLogExplorerTypes.SortOrder>('desc');
    const [wantCount, setWantCount] = useState(false);
    const [orgNames, setOrgNames] = useState<Record<string, string>>({});

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedMongoId, setSelectedMongoId] = useState<string | null>(null);

    // When this tab is hidden, close the detail drawer — it renders in a body-level
    // portal that would otherwise stay visible over the other tab.
    useEffect(() => {
        if (!active) { setDrawerOpen(false); setSelectedMongoId(null); }
    }, [active]);

    // Single query driver — fires for the initial load, each Search, and every
    // pagination / sort / "Load total" change.
    const runRef = useRef(0);
    useEffect(() => {
        const run = ++runRef.current;
        const body = buildSearchRequest(submitted, { page, limit, sortBy, sortOrder, withCount: wantCount });
        AuditLogExplorerService.search(body, (ok, msg) => {
            if (run !== runRef.current) return; // stale response
            if (!ok && msg) toast(msg, { type: 'error', autoClose: 3000 });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitted, page, limit, sortBy, sortOrder, wantCount]);

    const onSearch = useCallback(() => {
        setSubmitted({ ...draft });
        setPage(1);
        setWantCount(false);
    }, [draft]);

    const onClearAll = useCallback(() => {
        setDraft(EMPTY_FILTERS);
        setSubmitted({ ...EMPTY_FILTERS });
        setPage(1);
        setWantCount(false);
    }, []);

    // Pagination/sort keep the current filter set, so the total is unchanged —
    // clear wantCount after a "Load total" so we don't re-issue an expensive
    // COUNT on every subsequent page/sort/limit change.
    const onSort = useCallback((col: AuditLogExplorerTypes.SortBy) => {
        setPage(1);
        setWantCount(false);
        if (col === sortBy) {
            setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortBy(col);
            setSortOrder('desc');
        }
    }, [sortBy]);

    const onPageChange = useCallback((next: number) => {
        setPage(next);
        setWantCount(false);
    }, []);

    const onLimitChange = useCallback((l: number) => {
        setLimit(l);
        setPage(1);
        setWantCount(false);
    }, []);

    const onRemovePill = useCallback((key: AppliedPill['key']) => {
        const next = clearFilterKey(submitted, key);
        setSubmitted(next);
        setDraft((d) => clearFilterKey(d, key));
        setPage(1);
        setWantCount(false);
    }, [submitted]);

    const onRowClick = useCallback((mongoId: string) => {
        setSelectedMongoId(mongoId);
        setDrawerOpen(true);
        AuditLogExplorerService.getDetail(mongoId, (ok, msg) => {
            if (!ok && msg) toast(msg, { type: 'error', autoClose: 3000 });
        });
    }, []);

    const onExport = useCallback(() => {
        if (!result?.items?.length) {
            toast('Nothing to export yet — run a search first.', { type: 'info' });
            return;
        }
        downloadCsv(result.items);
    }, [result]);

    const appliedPills = useMemo(() => buildAppliedPills(submitted, orgNames), [submitted, orgNames]);

    return (
        <Box>
            <InfoBanner />
            <Card sx={{ mt: '18px', borderRadius: '8px', border: `1px solid ${ALE.border}`, boxShadow: ALE.shadowCard, overflow: 'visible' }}>
                <AuditFilterBar
                    filters={draft}
                    onPatch={(patch) => setDraft((d) => ({ ...d, ...patch }))}
                    onSearch={onSearch}
                    onExport={onExport}
                    searching={loading.search}
                    appliedPills={appliedPills}
                    onRemovePill={onRemovePill}
                    onClearAll={onClearAll}
                    onResolveOrgNames={(map) => setOrgNames((m) => ({ ...m, ...map }))}
                />
                <AuditResultsTable
                    result={result}
                    loading={loading.search}
                    page={page}
                    limit={limit}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={onSort}
                    onPageChange={onPageChange}
                    onLimitChange={onLimitChange}
                    onRowClick={onRowClick}
                    onLoadTotal={() => setWantCount(true)}
                    selectedMongoId={selectedMongoId}
                />
            </Card>

            <AuditDetailDrawer
                open={drawerOpen}
                loading={loading.detail}
                detail={detail}
                onClose={() => { setDrawerOpen(false); setSelectedMongoId(null); }}
            />
        </Box>
    );
};

// Re-exported so the page can label rows consistently if needed.
export { actionLabel };
